// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import { ActionSheetContext, LgPadding, ListButton, MdPadding, MdSpacing, Separator } from '@alto/design-system';
import { type InstructionalVideo as InstructionalVideoType } from '@alto/scriptdash/alto/patient_app/instructional_videos/types/v1/instructional_video';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { type Video } from '~shared/features/instructional-videos/types';
import { type OriginName } from '~shared/lib/analytics/src/getOrigin';
import { PreparationInfoActionSheet } from '../../med-details';
import { YoutubePlayer } from './YoutubePlayer';
import { parsePrepItems } from './utils';

type Props = {
  readonly index: number;
  readonly origin: OriginName;
  readonly video: Video | InstructionalVideoType;
  readonly isInCarousel?: boolean;
};

export const InstructionalVideo = ({ video, index, origin, isInCarousel = false }: Props) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  const videoID = 'videoID' in video ? video.videoID : video.youtube_video_id;
  const prepItems = 'prepItems' in video ? video.prepItems : parsePrepItems(video.prep_items);

  const { title } = video;

  const isWeb = Platform.OS === 'web';

  const handleShowPreparationInfo = () => {
    setActiveActionSheet(
      <PreparationInfoActionSheet
        title={title}
        prepItems={prepItems}
      />,
    );
  };

  // dynamically set width of the video player on web
  const containerRef = useRef(null);
  const [playerSize, setPlayerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!isWeb) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          const width = entry.contentRect.width;
          const height = (width / 16) * 9; // maintain 16:9 aspect ratio
          setPlayerSize({ width, height });
        }
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isWeb]);

  const Padding = isInCarousel && isWeb ? LgPadding : MdPadding;

  return (
    <>
      {!isInCarousel && index !== 0 && <Separator />}
      <Padding bottomPadding={SPACING.STATIC.NONE}>
        <View
          ref={containerRef}
          onLayout={
            isWeb
              ? undefined
              : (event) => {
                  const { width } = event.nativeEvent.layout;
                  const height = (width / 16) * 9; // maintain 16:9 aspect ratio
                  setPlayerSize({ width, height });
                }
          }
        >
          <YoutubePlayer
            title={title}
            videoID={videoID}
            width={playerSize.width}
            height={playerSize.height}
            origin={origin}
          />
        </View>
        <MdSpacing />
        <ListButton
          label="Preparation info"
          rightIcon="chevronright-small"
          onPress={handleShowPreparationInfo}
        />
        <MdSpacing />
      </Padding>
    </>
  );
};
